"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, MapPin, Users, Medal, Target } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const INFO_CARD_CLASSES = cn(
  "shadow-lg border-muted/20 bg-background/80 dark:bg-card/80 backdrop-blur-sm",
  "hover:bg-background/90 dark:hover:bg-card/90 transition-colors"
);
const ACCORDION_ITEM_CLASSES =
  "border-muted/20 bg-card rounded-xl px-4 sm:px-6 shadow-sm";
const SUB_CARD_CLASSES =
  "p-4 bg-muted/30 rounded-lg border border-muted/20 hover:bg-muted/40 transition-colors";

export function SovenskoLegend() {
  const SECTION_TITLE = "O turnaji";
  const ABOUT_DESC =
    "Hokejový turnaj na ZOH 2026 v Miláne bude siedmou účasťou Slovenska na zimných olympijských hrách od samostatnosti. Slovensko obhajuje bronzovú medailu z Pekingu 2022.";
  const FORMAT_TITLE = "Formát turnaja";
  const ARENAS_TITLE = "Štadióny";
  const HISTORY_TITLE = "História Slovenska na ZOH";
  const BRONZE_LABEL = "Bronz 2022";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-8"
    >
      {/* Hlavné informácie */}
      <div className="flex items-center gap-3">
        <Trophy className="h-6 w-6 text-primary" />
        <h2 className="text-2xl font-bold">{SECTION_TITLE}</h2>
      </div>

      <Card className={INFO_CARD_CLASSES}>
        <CardContent className="pt-6">
          <div className="prose dark:prose-invert max-w-none">
            <p className="text-muted-foreground leading-relaxed">
              {ABOUT_DESC}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Rozšírené informácie v accordion */}
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem
          value="format"
          className={cn(ACCORDION_ITEM_CLASSES, "mb-4")}
        >
          <AccordionTrigger className="hover:no-underline py-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-status-info/15 rounded-lg text-status-info">
                <Target className="h-5 w-5" />
              </div>
              <span className="font-bold">{FORMAT_TITLE}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="text-muted-foreground leading-relaxed">
            <div className="space-y-4 pt-2">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className={SUB_CARD_CLASSES}>
                  <h4 className="font-bold text-foreground mb-2 flex items-center gap-2">
                    <Badge
                      variant="default"
                      className="bg-status-success text-status-success-foreground border-none"
                    >
                      Základná časť
                    </Badge>
                    3 skupiny
                  </h4>
                  <ul className="list-disc list-inside text-sm space-y-1.5">
                    <li>12 krajín rozdelených do 3 skupín po 4 tímy</li>
                    <li>Každý s každým v rámci skupiny</li>
                  </ul>
                </div>
                <div className={SUB_CARD_CLASSES}>
                  <h4 className="font-bold text-foreground mb-2 flex items-center gap-2">
                    <Badge variant="secondary" className="border-none">
                      Play-off
                    </Badge>
                    Postupový systém
                  </h4>
                  <ul className="list-disc list-inside text-sm space-y-1.5">
                    <li>1.–4. miesto: priamy postup do štvrťfinále</li>
                    <li>5.–12. miesto: kvalifikačné predkolo (osemfinále)</li>
                    <li>Páry: 5–12, 6–11, 7–10, 8–9</li>
                  </ul>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="stadiony"
          className={cn(ACCORDION_ITEM_CLASSES, "mb-4")}
        >
          <AccordionTrigger className="hover:no-underline py-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-status-success/15 rounded-lg text-status-success">
                <MapPin className="h-5 w-5" />
              </div>
              <span className="font-bold">{ARENAS_TITLE}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="text-muted-foreground leading-relaxed">
            <div className="space-y-4 pt-2">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className={SUB_CARD_CLASSES}>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-4 w-4 text-status-success" />
                    <h4 className="font-bold text-foreground">
                      Santagiulia Ice Hockey Arena
                    </h4>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>
                      Kapacita: <strong>14 000</strong> divákov
                    </span>
                  </div>
                </div>
                <div className={SUB_CARD_CLASSES}>
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-4 w-4 text-status-success" />
                    <h4 className="font-bold text-foreground">
                      Rho Ice Hockey Arena
                    </h4>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>
                      Kapacita: <strong>5 700</strong> divákov
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="história" className={ACCORDION_ITEM_CLASSES}>
          <AccordionTrigger className="hover:no-underline py-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-status-warning/15 rounded-lg text-status-warning">
                <Medal className="h-5 w-5" />
              </div>
              <span className="font-bold">{HISTORY_TITLE}</span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="text-muted-foreground leading-relaxed">
            <div className="space-y-4 pt-2">
              <p>
                Slovensko sa zúčastňuje hokejového turnaja na ZOH od roku 1994.
                Najväčším úspechom je bronzová medaila z Pekingu 2022.
              </p>
              <div className={SUB_CARD_CLASSES}>
                <h4 className="font-bold text-foreground mb-2 flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="border-status-warning/30 text-status-warning bg-status-warning/5"
                  >
                    🥉 {BRONZE_LABEL}
                  </Badge>
                  Peking
                </h4>
                <p className="text-sm">
                  Historicky prvý medailový zisk Slovenska na olympijskom
                  hokejovom turnaji.
                </p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </motion.div>
  );
}
